/**
 * STORY-006-FUP-1 — URL state adapter.
 *
 * `<ProductFilters>` needs to read + write filter state out of the URL. On
 * the React-Router SPA surfaces (`/nl/products`, `/nl/categories/:slug`
 * when Vike isn't matched) we use `useSearchParams` from react-router,
 * which does an in-place route update. On the Vike SSR surface there is a
 * react-router context (ShopChromeLayout wraps every page in
 * BrowserRouter), BUT the `NavigationInterceptor` in that layout rewrites
 * every `history.pushState` call into a full `window.location.assign`
 * reload — so letting the filters call `setSearchParams` from react-router
 * would hard-reload the page on every toggle.
 *
 * The adapter lets each call-site supply the right URL-state plumbing:
 *
 *   - SPA surfaces pass `reactRouterAdapter()` (defaults to `useSearchParams`).
 *   - Vike SSR surfaces pass `useVikeUrlAdapter()` (below), which uses the
 *     native `history.pushState` (bypassing the intercepting patched one
 *     by calling `window.history.pushState.call` on the ORIGINAL).
 *
 * Keeps `<ProductFilters>` free of any router dependency — the only thing
 * it sees is `{ searchParams, setSearchParams }`.
 */
import { useCallback, useEffect, useRef, useState } from 'react'

export interface UrlStateAdapter {
  searchParams: URLSearchParams
  setSearchParams: (next: URLSearchParams) => void
}

/**
 * Vike-surface URL state adapter.
 *
 * On the server: returns an empty URLSearchParams (SSR can't read
 * window.location, but the initial filter state that matters for first
 * paint is already derived inside `+data.ts` and passed in as `facets`
 * + selected URL params from `pageContext.urlOriginal`). We accept that
 * initial state as the `initial` argument.
 *
 * On the client: reads `window.location.search` once on mount, then
 * updates via direct `history.pushState` (bypassing the patched one in
 * ShopChromeLayout's NavigationInterceptor). Listens for `popstate` so
 * browser back/forward within the same page updates the filters without
 * a full reload.
 *
 * NOTE: the adapter intentionally does NOT trigger a full navigation —
 * that's the whole point. Callers (e.g. `<CategoryDetailView>`) are
 * expected to re-run their data queries when the URL changes; we expose
 * the updated `URLSearchParams` snapshot as React state so TanStack
 * Query re-derives its key automatically.
 */
export function useVikeUrlAdapter(initial?: string): UrlStateAdapter {
  const [params, setParams] = useState<URLSearchParams>(() => {
    if (typeof window === 'undefined') {
      return new URLSearchParams(initial ?? '')
    }
    return new URLSearchParams(window.location.search)
  })

  // Keep a stable reference to the *original* pushState so we can bypass
  // the ShopChromeLayout NavigationInterceptor (which otherwise rewrites
  // every pushState into a full page reload). We capture once on mount.
  const pushStateRef = useRef<typeof window.history.pushState | null>(null)

  useEffect(() => {
    if (typeof window === 'undefined') return
    // At this point NavigationInterceptor has already replaced
    // window.history.pushState with its patched version. We don't have
    // access to the original from here — BUT `History.prototype.pushState`
    // is still the untouched native one (the interceptor patches the
    // instance, not the prototype). Use that to bypass.
    const nativePushState = History.prototype.pushState.bind(window.history)
    pushStateRef.current = nativePushState

    function onPopState() {
      setParams(new URLSearchParams(window.location.search))
    }
    window.addEventListener('popstate', onPopState)
    return () => {
      window.removeEventListener('popstate', onPopState)
    }
  }, [])

  const setSearchParams = useCallback((next: URLSearchParams) => {
    const queryString = next.toString()
    if (typeof window !== 'undefined' && pushStateRef.current) {
      const url =
        window.location.pathname + (queryString ? `?${queryString}` : '') + window.location.hash
      pushStateRef.current({}, '', url)
    }
    // Always update React state — on the server this is the initial
    // snapshot; on the client this is the new snapshot that keys our
    // TanStack queries.
    setParams(new URLSearchParams(queryString))
  }, [])

  return { searchParams: params, setSearchParams }
}

// ─── Per-category attribute filter helpers ───────────────────────────────────
// Round-trip `attribute[<key>][]=<value>` between URL and state.
//
// We rely on URLSearchParams (`getAll` / `append`) end-to-end — no
// hand-rolled string parsing. The bracket characters in the key are
// percent-encoded by `URLSearchParams.toString()` and decoded back by
// `new URLSearchParams(qs)` symmetrically, so the round-trip is lossless
// even though the raw URL string ends up containing `%5B` / `%5D`.

const ATTRIBUTE_PARAM_RE = /^attribute\[(.+)\]\[\]$/

export function attributeParamPrefix(attributeKey: string): string {
  return `attribute[${attributeKey}][]`
}

/**
 * Extract every `attribute[<key>][]=<value>` triple from a URLSearchParams
 * snapshot. Keys whose param matches the bracket pattern are decoded back
 * to their bare attribute key; everything else is ignored.
 *
 * Returns a fresh object — never aliased to internal state — so callers
 * can mutate the result without affecting `searchParams`.
 */
export function readAttributesFromParams(
  params: URLSearchParams,
): Record<string, string[]> {
  const out: Record<string, string[]> = {}
  // Iterating with .keys() can yield the same key multiple times — track
  // the ones we've already collected to avoid an O(n²) getAll() loop.
  const seen = new Set<string>()
  for (const rawKey of params.keys()) {
    if (seen.has(rawKey)) continue
    seen.add(rawKey)
    const m = ATTRIBUTE_PARAM_RE.exec(rawKey)
    if (!m) continue
    const attrKey = m[1]
    const values = params.getAll(rawKey).filter((v) => v.length > 0)
    if (values.length > 0) out[attrKey] = values
  }
  return out
}

/**
 * Replace every `attribute[*][]` param on `params` with the entries from
 * `attributes`. Mutates `params` in place. Empty value arrays are dropped
 * entirely, so passing `{}` strips all attribute params (used by
 * `clearAll`).
 */
export function setAttributesOnParams(
  params: URLSearchParams,
  attributes: Record<string, readonly string[]>,
): void {
  // Snapshot the keys to delete first — modifying while iterating
  // params.keys() is implementation-defined.
  const toDelete: string[] = []
  for (const k of params.keys()) {
    if (ATTRIBUTE_PARAM_RE.test(k)) toDelete.push(k)
  }
  // dedupe — params.keys() can yield repeats for repeated params
  const uniqueDelete = Array.from(new Set(toDelete))
  for (const k of uniqueDelete) params.delete(k)

  for (const [attrKey, values] of Object.entries(attributes)) {
    if (!values || values.length === 0) continue
    const paramKey = attributeParamPrefix(attrKey)
    for (const v of values) {
      if (v.length === 0) continue
      params.append(paramKey, v)
    }
  }
}
